// Port of TrailMap/GeoAdminWildlifeRestZones.swift (TrailBivvyKit): Swiss wildlife rest zones ("Wildruhezonen", layer
// `ch.bafu.wrz-wildruhezonen_portal`) from the geo.admin.ch REST `identify` service (sr=4326, geometryFormat=geojson).
//
// Mapping to `ProtectedArea` (regulatory honesty first):
// - kind `wildlifeRestZone`, confidence `medium` (official aggregate, but the FOEN legend states the dataset is
//   incomplete and only cantonal decisions are legally binding), provenance BAFU/FOEN via geo.admin.ch with licence
//   review `pendingHumanReview`.
// - `overnightRule`: a legally binding zone ("rechtsverbindlich") with an access ban (`bestimmung` R10) or whose provision
//   text bans camping/bivouacking → `prohibited`. A binding zone with R40/R80 stays `unknown` for the night itself but
//   its access rule already resolves to a restriction. Any other binding zone (R900 "other provision", or an
//   unrecognised code) → `restricted`. Recommended zones ("empfohlen") are never mapped to a restriction or
//   prohibition; they stay `unknown`.
// - `accessRule`: R10 → `noAccess`, R40/R80 → `stayOnMarkedTrails`, otherwise `unknown`.
// - The meaning of the `bestimmung` codes comes from observed attribute values and has not been verified against the
//   FOEN data model (needs human check). Every mapping can only make the resolved state more restrictive.
// - `seasonalWindow` from `schutzzeit` ("01.12. - 20.04."); year-round, unparsable or multi-period values → undefined
//   (always active, the conservative reading).
// Permitted-route line features of the layer are skipped.
//
// Web addition: `FairUseLimiter`. The FSDI terms limit the REST API to 40 requests per minute; Swift only capped the
// pages per query. Every identify request first takes a slot from `geoAdminRESTLimiter` (shared by every caller of the
// geo.admin.ch REST API in the app); without a free slot the request is not sent (reported as HTTP 429, which callers
// treat like any network failure: local data is used).

import type {
  AccessRule,
  DataProvenance,
  EpochMs,
  GeoBounds,
  GeoCoordinate,
  GeoPolygon,
  Jurisdiction,
  OvernightRule,
  ProtectedArea,
  SeasonalWindow,
} from '../core/models';
import { isValidCoordinate } from '../core/geo';
import { makeHTTPRequest, responseText, type HTTPClient } from '../net/httpTypes';
import { NetworkFailure } from '../net/networkError';

/** Sliding-window request budget (default: the FSDI REST fair-use limit, 40 requests per 60 s). */
export class FairUseLimiter {
  private readonly starts: number[] = [];

  constructor(
    readonly maxRequests = 40,
    readonly windowMs = 60_000,
    private readonly clock: () => number = () => Date.now(),
  ) {}

  /** Takes a slot when one is free in the current window; `false` means "do not send now". */
  tryAcquire(): boolean {
    const now = this.clock();
    while (this.starts.length > 0 && now - this.starts[0] >= this.windowMs) this.starts.shift();
    if (this.starts.length >= this.maxRequests) return false;
    this.starts.push(now);
    return true;
  }

  /** Requests still allowed in the current window. */
  get remaining(): number {
    const now = this.clock();
    return this.maxRequests - this.starts.filter((t) => now - t < this.windowMs).length;
  }
}

/** One budget for every geo.admin.ch REST call made by this page (FSDI: 40 requests/minute). */
export const geoAdminRESTLimiter = new FairUseLimiter();

const identifyEndpoint = 'https://api3.geo.admin.ch/rest/services/api/MapServer/identify';

type JSONObject = Record<string, unknown>;

function isObject(value: unknown): value is JSONObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Query value encoding like Foundation's URLComponents (commas and colons kept). */
function queryValue(value: string): string {
  return encodeURIComponent(value).replace(/%2C/gi, ',').replace(/%3A/gi, ':');
}

export const GeoAdminWildlifeRestZones = {
  /** geo.admin.ch layer id. */
  layerID: 'ch.bafu.wrz-wildruhezonen_portal',
  /** Maximum features per identify request (documented service maximum). */
  pageSize: 200,
  /** Maximum pages fetched per query (keeps within the 40 requests/minute REST fair-use limit). */
  maximumPages: 3,
  sourcePage: 'https://map.geo.admin.ch/?layers=ch.bafu.wrz-wildruhezonen_portal',

  /** Identify request URL for all zones intersecting `bounds` (WGS84 envelope, GeoJSON geometries, English labels). */
  identifyURL(bounds: GeoBounds, limit = 200, offset = 0): string | undefined {
    const envelope = [bounds.minLongitude, bounds.minLatitude, bounds.maxLongitude, bounds.maxLatitude];
    if (!envelope.every(Number.isFinite)) return undefined;
    const envelopeText = envelope.map((v) => v.toFixed(6)).join(',');
    const items: Array<[string, string]> = [
      ['geometryType', 'esriGeometryEnvelope'],
      ['geometry', envelopeText],
      ['sr', '4326'],
      ['imageDisplay', '1000,1000,96'],
      ['mapExtent', envelopeText],
      ['tolerance', '0'],
      ['layers', `all:${GeoAdminWildlifeRestZones.layerID}`],
      ['returnGeometry', 'true'],
      ['geometryFormat', 'geojson'],
      ['lang', 'en'],
      ['limit', String(Math.max(1, limit))],
      ['offset', String(Math.max(0, offset))],
    ];
    return `${identifyEndpoint}?${items.map(([k, v]) => `${k}=${queryValue(v)}`).join('&')}`;
  },

  /** Parses an identify response into protected areas. Throws `NetworkFailure` `decoding` for a malformed payload. */
  parse(data: Uint8Array | string, fetchedAt: EpochMs): ProtectedArea[] {
    return GeoAdminWildlifeRestZones.parsePage(data, fetchedAt).areas;
  },

  /**
   * Fetches all pages for `bounds` (up to `maximumPages`), de-duplicated by id. Throws `NetworkFailure`. Each request
   * takes a slot from `limiter` first.
   */
  async fetch(
    bounds: GeoBounds,
    client: HTTPClient,
    fetchedAt: EpochMs,
    limiter: FairUseLimiter = geoAdminRESTLimiter,
  ): Promise<ProtectedArea[]> {
    const areas: ProtectedArea[] = [];
    const seen = new Set<string>();
    const { pageSize, maximumPages } = GeoAdminWildlifeRestZones;
    for (let page = 0; page < maximumPages; page++) {
      const url = GeoAdminWildlifeRestZones.identifyURL(bounds, pageSize, page * pageSize);
      if (!url) throw new NetworkFailure({ kind: 'invalidURL' });
      if (!limiter.tryAcquire()) {
        if (page === 0) throw new NetworkFailure({ kind: 'http', status: 429 });
        console.info('Wildlife rest zones: REST fair-use budget used up, result truncated');
        return areas;
      }
      const response = await client.send(makeHTTPRequest(url, { headers: { Accept: 'application/json' }, timeout: 20 }));
      const parsed = GeoAdminWildlifeRestZones.parsePage(response.data, fetchedAt);
      for (const area of parsed.areas) {
        if (seen.has(area.id)) continue;
        seen.add(area.id);
        areas.push(area);
      }
      if (parsed.resultCount < pageSize) return areas;
    }
    // Every page was full: more zones may exist than were fetched. The result is incomplete, so absence of a zone here
    // must not be read as "no zone" (the map layer is informational; regulatory state comes from the offline dataset).
    console.info(`Wildlife rest zones truncated at ${maximumPages * pageSize} features; narrow the query`);
    return areas;
  },

  // MARK: - Parsing

  parsePage(data: Uint8Array | string, fetchedAt: EpochMs): { areas: ProtectedArea[]; resultCount: number } {
    const text = typeof data === 'string' ? data : responseText({ data, statusCode: 200, headers: {}, url: '' });
    let root: unknown;
    try {
      root = JSON.parse(text);
    } catch {
      throw new NetworkFailure({ kind: 'decoding', message: 'geo.admin.ch identify: invalid JSON' });
    }
    if (!isObject(root) || !Array.isArray(root.results) || !root.results.every(isObject)) {
      throw new NetworkFailure({ kind: 'decoding', message: "geo.admin.ch identify: missing 'results'" });
    }
    const results = root.results as JSONObject[];
    const areas: ProtectedArea[] = [];
    for (const feature of results) {
      const area = GeoAdminWildlifeRestZones.zone(feature, fetchedAt);
      if (area) areas.push(area);
    }
    return { areas, resultCount: results.length };
  },

  zone(feature: JSONObject, fetchedAt: EpochMs): ProtectedArea | undefined {
    const properties: JSONObject = isObject(feature.properties)
      ? feature.properties
      : isObject(feature.attributes)
        ? feature.attributes
        : {};
    // Zones carry a protection status; the layer's permitted-route lines carry `wegtyp_*` instead.
    if (properties.schutzs_de === undefined && properties.wrz_name === undefined) return undefined;
    if (!isObject(feature.geometry)) return undefined;
    const polygons = GeoAdminWildlifeRestZones.polygons(feature.geometry);
    const identifier = GeoAdminWildlifeRestZones.featureIdentifier(feature);
    if (polygons.length === 0 || identifier === undefined) return undefined;

    const text = GeoAdminWildlifeRestZones.text;
    const status = text(properties.schutzs_de)?.toLowerCase() ?? '';
    const isBinding = status.includes('rechtsverbindlich');
    const provision = text(properties.bestimmung)?.toUpperCase() ?? '';
    const provisionTexts = ['zusatzinformation', 'best_de', 'best_fr', 'best_it']
      .map((key) => text(properties[key]))
      .filter((t): t is string => t !== undefined);
    const bansCamping = provisionTexts.some(GeoAdminWildlifeRestZones.statesCampingProhibition);
    let overnightRule: OvernightRule;
    if (isBinding && (provision === 'R10' || bansCamping)) {
      overnightRule = 'prohibited';
    } else if (isBinding && provision !== 'R40' && provision !== 'R80') {
      // Binding, but the provision is "other" or unknown: a restriction exists; never grey "no data".
      overnightRule = 'restricted';
    } else {
      overnightRule = 'unknown';
    }
    let accessRule: AccessRule = 'unknown';
    if (isBinding && provision === 'R10') accessRule = 'noAccess';
    else if (isBinding && (provision === 'R40' || provision === 'R80')) accessRule = 'stayOnMarkedTrails';

    const canton = text(properties.kanton)?.toUpperCase();
    const jurisdiction: Jurisdiction = canton
      ? { level: 'cantonal', countryCode: 'CH', name: `Canton ${canton}`, code: canton }
      : { level: 'federal', countryCode: 'CH', name: 'FOEN/BAFU' };
    const name = text(properties.wrz_name) ?? text(properties.label) ?? `Wildruhezone ${identifier}`;

    const area: ProtectedArea = {
      id: `geoadmin:${GeoAdminWildlifeRestZones.layerID}:${identifier}`,
      name,
      kind: 'wildlifeRestZone',
      polygons,
      countryCode: 'CH',
      jurisdiction,
      overnightRule,
      accessRule,
      regulationIDs: [],
      isSynthetic: false,
      provenance: GeoAdminWildlifeRestZones.provenance(fetchedAt),
      confidence: 'medium',
    };
    const window = GeoAdminWildlifeRestZones.seasonalWindow(text(properties.schutzzeit));
    if (window) area.seasonalWindow = window;
    return area;
  },

  provenance(fetchedAt: EpochMs): DataProvenance {
    return {
      provider: 'BAFU/FOEN and cantons via geo.admin.ch',
      dataset: GeoAdminWildlifeRestZones.layerID,
      sourceURL: GeoAdminWildlifeRestZones.sourcePage,
      license: 'Open use, source must be provided (opendata.swiss BY); FSDI terms of use',
      attribution: '© BAFU/FOEN, cantons',
      fetchedAt,
      isSynthetic: false,
      licenseReviewStatus: 'pendingHumanReview',
    };
  },

  /** True when a sentence both mentions camping/bivouacking and states a ban (DE/FR/IT/EN wording). */
  statesCampingProhibition(text: string): boolean {
    const subjects = ['campieren', 'camping', 'biwak', 'zelten', 'bivouac', 'bivacc', 'campeggi'];
    const bans = [
      'verboten',
      'untersagt',
      'nicht gestattet',
      'nicht erlaubt',
      'interdit',
      'vietat',
      'non è consentit',
      'non consentit',
      'prohibited',
      'not permitted',
      'not allowed',
    ];
    const sentences = text
      .toLowerCase()
      .split(/[.;\n]/)
      .filter((s) => s.length > 0);
    return sentences.some((sentence) => subjects.some((s) => sentence.includes(s)) && bans.some((b) => sentence.includes(b)));
  },

  /**
   * Parses "DD.MM. - DD.MM." into a seasonal window; undefined for year-round (01.01.–31.12.), multi-period or
   * unparsable values (callers treat undefined as "always active").
   */
  seasonalWindow(period: string | undefined): SeasonalWindow | undefined {
    if (period === undefined) return undefined;
    const pattern = /(\d{1,2})\.(\d{1,2})\.?\s*[-–—]\s*(\d{1,2})\.(\d{1,2})\.?/g;
    const matches = [...period.matchAll(pattern)];
    // Several periods cannot be represented by one window: keeping only the first would leave the zone inactive
    // during the others. Treat the zone as active all year instead.
    if (matches.length !== 1) return undefined;
    const match = matches[0];
    const startDay = Number(match[1]);
    const startMonth = Number(match[2]);
    const endDay = Number(match[3]);
    const endMonth = Number(match[4]);
    const month = (m: number) => m >= 1 && m <= 12;
    const day = (d: number) => d >= 1 && d <= 31;
    if (!month(startMonth) || !month(endMonth) || !day(startDay) || !day(endDay)) return undefined;
    if (startMonth === 1 && startDay === 1 && endMonth === 12 && endDay === 31) return undefined;
    return { startMonth, startDay, endMonth, endDay };
  },

  /** Polygon / MultiPolygon GeoJSON (lon, lat) → polygons; rings with < 3 valid WGS84 points are dropped. */
  polygons(geometry: JSONObject): GeoPolygon[] {
    const coordinates = geometry.coordinates;
    switch (geometry.type) {
      case 'Polygon': {
        if (!isRings(coordinates)) return [];
        const polygon = GeoAdminWildlifeRestZones.polygon(coordinates);
        return polygon ? [polygon] : [];
      }
      case 'MultiPolygon': {
        if (!Array.isArray(coordinates) || !coordinates.every(isRings)) return [];
        return (coordinates as number[][][][])
          .map((rings) => GeoAdminWildlifeRestZones.polygon(rings))
          .filter((p): p is GeoPolygon => p !== undefined);
      }
      default:
        return [];
    }
  },

  polygon(rings: number[][][]): GeoPolygon | undefined {
    const converted = rings.map((ring) =>
      ring
        .filter((position) => position.length >= 2)
        .map((position): GeoCoordinate => ({ latitude: position[1], longitude: position[0] }))
        .filter(isValidCoordinate),
    );
    const exterior = converted[0];
    if (!exterior || exterior.length < 3) return undefined;
    return { exterior, holes: converted.slice(1).filter((h) => h.length >= 3) };
  },

  featureIdentifier(feature: JSONObject): string | undefined {
    for (const key of ['featureId', 'id']) {
      const value = feature[key];
      if (typeof value === 'string' && value !== '') return value;
      if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    }
    return undefined;
  },

  text(value: unknown): string | undefined {
    if (typeof value !== 'string') return undefined;
    const trimmed = value.trim();
    return trimmed === '' ? undefined : trimmed;
  },
};

/** `[[[lon, lat], …], …]` with numbers only (Swift's `as? [[[Double]]]`). */
function isRings(value: unknown): value is number[][][] {
  return (
    Array.isArray(value) &&
    value.every(
      (ring) => Array.isArray(ring) && ring.every((p) => Array.isArray(p) && p.every((n) => typeof n === 'number')),
    )
  );
}
